import React, { useState } from "react";
import { Query } from "react-apollo";
import { Button, Icon, Modal, notification, Upload } from "antd";
import * as R from "ramda";
import { Tunnel } from "react-tunnels";
import Heading from "@arachnys/styleguide/Heading";
import Table from "@arachnys/styleguide/Table";

import { RESEARCH_REQUEST_ATTACHMENTS } from "../queries";
import { formatDatetimeString } from "../../../formatters";
import { BASE_URL } from "../../../config";
import { authFetch } from "../../../api";
import {
    downloadFile,
    parseFilenameFromContentDisposition,
    withApolloState,
} from "../../../util";

const Results = props => (
    <>
        <Heading level={1}>Continuous Monitoring Results</Heading>
        <UploadFileQuery
            researchRequestUlid={props.researchRequestUlid}
            auth={props.auth}
        />
    </>
);

const UploadFileQuery = props => {
    const [visible, toggleVisibility] = useState(false);
    return (
        <Query
            query={RESEARCH_REQUEST_ATTACHMENTS}
            fetchPolicy="cache-and-network"
            variables={{
                id: btoa("ResearchRequest:" + props.researchRequestUlid),
                isContinuousMonitoring: true,
            }}
        >
            {withApolloState(apolloProps => {
                const data = R.pathOr(
                    [],
                    ["researchRequest", "fileUploads"],
                    apolloProps.data
                );
                const canManageCmFiles = R.pathOr(
                    false,
                    ["permissions", "task", "canManageCmFiles"],
                    apolloProps.data
                );

                return (
                    <>
                        <FileTable
                            canManageCmFiles={canManageCmFiles}
                            loading={apolloProps.loading}
                            data={data}
                            researchRequestUlid={props.researchRequestUlid}
                            refetch={apolloProps.refetch}
                        />
                        {canManageCmFiles && (
                            <Tunnel id="bluebar-right-content">
                                <Button
                                    size="large"
                                    onClick={() => toggleVisibility(true)}
                                    ghost
                                >
                                    Upload files
                                </Button>
                                <UploadFileComponent
                                    visible={visible}
                                    toggleVisibility={toggleVisibility}
                                    auth={props.auth}
                                    researchRequestUlid={
                                        props.researchRequestUlid
                                    }
                                    refetchFiles={apolloProps.refetch}
                                />
                            </Tunnel>
                        )}
                    </>
                );
            })}
        </Query>
    );
};

class FileTable extends React.Component {
    columns = [
        {
            id: "fileName",
            Header: "File name",
            sortable: false,
            accessor: d => (
                <a href="#" onClick={e => this.handleDownload(d.objectKey, e)}>
                    {d.fileName}
                </a>
            ),
        },
        {
            id: "author",
            Header: "Uploaded by",
            width: 350,
            sortable: false,
            accessor: d =>
                d.author.lastName && d.author.firstName
                    ? `${d.author.lastName} ${d.author.firstName}`
                    : d.author.username,
        },
        {
            id: "createdAt",
            Header: "Date",
            width: 200,
            sortable: false,
            accessor: d => formatDatetimeString({ str: Date(d.createdAt) }),
        },
        {
            id: "action",
            width: 50,
            sortable: false,
            accessor: d =>
                this.props.canManageCmFiles && (
                    <Icon
                        style={{ cursor: "pointer" }}
                        title="delete"
                        type="delete"
                        onClick={() =>
                            this.removeDocument(d.objectKey, d.fileName)
                        }
                    />
                ),
        },
    ];

    handleDownload = async (id, e) => {
        e.preventDefault();
        const req = new Request(
            `${BASE_URL}/api/tickets/${this.props.researchRequestUlid}/attachments/${id}/`
        );
        try {
            await this.download(await authFetch(req));
        } catch (e) {
            this.setState({ downloadError: e });
        }
    };

    async download(response) {
        downloadFile(this.getFilename(response))(response);
    }

    getFilename(response) {
        let filename = "download";
        const cd = response.headers.get("content-disposition");
        if (cd) {
            filename = parseFilenameFromContentDisposition(cd);
        }
        return filename;
    }

    async removeDocument(objectKey, name) {
        await this.showDeleteConfirm(name);
        const { researchRequestUlid: rrUlid } = this.props;
        const deleteUrl = `/api/tickets/${rrUlid}/attachments/${objectKey}/delete/?is_continuous_monitoring=true`;
        const delReq = new Request(deleteUrl, {
            method: "POST",
        });
        try {
            await authFetch(delReq);
        } catch (e) {
            notification.error({
                message: "Permission denied",
                description: "Only analysts and managers can remove files",
            });
        }
        return true;
    }

    showDeleteConfirm(description) {
        return new Promise((res, rej) => {
            const box = Modal.confirm({
                title: "Do you want to delete this CM result",
                content: `Are you sure you want to delete "${description}"?`,
                onOk: () => {
                    box.destroy();
                    res();
                    this.props.refetch();
                },
                onCancel: () => {
                    box.destroy();
                    rej();
                },
            });
        });
    }

    render() {
        const recordsNumber = this.props.data.length;
        const pageSize = Math.min(recordsNumber, 10) || 10;
        return (
            <Table
                data={this.props.data}
                loading={this.props.loading}
                style={{ paddingTop: "1em" }}
                pageSize={pageSize}
                showPagination={recordsNumber > pageSize}
                columns={this.columns}
            />
        );
    }
}

const MAX_FILE_SIZE = 10;
class UploadFileComponent extends React.Component {
    state = {
        fileList: [],
    };

    onFileUploadChange = event => {
        const { status } = event.file;
        if (status !== "uploading") {
            this.setState({ fileList: [] }, () =>
                this.props.toggleVisibility(false)
            );
            if (status === "error") {
                const errorMessage = R.pathOr(
                    "Failed to upload file",
                    ["response", "message"],
                    event.file
                );
                notification.error({
                    message: "Error during file upload",
                    description: errorMessage,
                });
            } else if (status === "done") {
                this.props.refetchFiles();
            }
        } else {
            this.setState({ fileList: event.fileList });
        }
    };

    validateFile(file) {
        const isLtMaxSize = file.size / 1024 / 1024 < MAX_FILE_SIZE;
        if (!isLtMaxSize) {
            notification.error({
                message: "File is too large",
                description: `File must be smaller than ${MAX_FILE_SIZE}MB!`,
            });
        }
        return isLtMaxSize;
    }

    render() {
        const {
            visible,
            toggleVisibility,
            auth,
            researchRequestUlid,
        } = this.props;
        const headers = {
            authorization: "Bearer " + auth.accessToken,
        };
        const submitAction = `${BASE_URL}/api/tickets/${researchRequestUlid}/attachments/?is_continuous_monitoring=true`;
        return (
            <Modal
                visible={visible}
                title="Continuous Monitoring Results Upload"
                footer=""
                onCancel={() => toggleVisibility(false)}
            >
                <Upload.Dragger
                    multiple
                    accept="image/*,.doc,.docx,.pdf,.csv"
                    beforeUpload={this.validateFile}
                    headers={headers}
                    action={submitAction}
                    fileList={this.state.fileList}
                    onChange={this.onFileUploadChange}
                >
                    <p className="ant-upload-drag-icon">
                        <Icon type="inbox" />
                    </p>
                    <p className="ant-upload-text">
                        Click or drag file to this area to upload
                    </p>
                    <p className="ant-upload-hint">
                        {"Valid file format：.pdf, .doc, .docx, .csv, image/*"}
                    </p>
                </Upload.Dragger>
            </Modal>
        );
    }
}

export default Results;
